// KB entry row — compact list view with expandable content, images, chunks
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FileText, Trash2, Eye, EyeOff, Image } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface KBEntryCardProps {
  onDelete?: (id: string) => void
  entry: {
    id: string
    title: string
    description: string | null
    content: string | null
    category: string
    source_type: string
    source_id?: string | null
    d365_versions: string[]
    d365_modules: string[]
    is_validated: boolean | null
    created_at: string
  }
}

function decodeHtml(text: string): string {
  return text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
}

export function KBEntryCard({ entry, onDelete }: KBEntryCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false)

  const docQuery = useQuery({
    queryKey: ['kb-doc-meta', entry.source_id],
    queryFn: async () => {
      if (!entry.source_id) return null
      const { data } = await supabase.from('customer_profile_documents')
        .select('metadata').eq('id', entry.source_id).single()
      return data
    },
    enabled: expanded && !!entry.source_id,
  })

  const embeddedImages = (docQuery.data?.metadata as Record<string, unknown>)?.embedded_images as
    { name: string; path: string; mimeType: string; ocrText: string }[] | undefined

  const chunksQuery = useQuery({
    queryKey: ['kb-entry-chunks', entry.id, entry.source_id],
    queryFn: async () => {
      if (!entry.source_id || entry.source_type !== 'document_import') return []
      const { data } = await (supabase as never as { from: (t: string) => ReturnType<typeof supabase.from> })
        .from('customer_profile_document_chunks')
        .select('id, chunk_index, content, token_count')
        .eq('document_id', entry.source_id)
        .order('chunk_index', { ascending: true })
      return (data ?? []) as { id: string; chunk_index: number; content: string; token_count: number }[]
    },
    enabled: expanded && entry.source_type === 'document_import' && !!entry.source_id,
  })

  const sourceLabel = entry.source_type === 'document_import' ? 'Document'
    : entry.source_type === 'issue_resolution' ? 'Resolution'
    : entry.source_type === 'manual' ? 'Manual' : entry.source_type

  return (
    <div className="rounded-lg border border-kaycoo-white-3 bg-kaycoo-white overflow-hidden">
      {/* Row — single line with title, badges, actions */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-kaycoo-charcoal truncate">
              {decodeHtml(entry.title)}
            </span>
            <Badge variant="outline" className="text-[10px] shrink-0 bg-kaycoo-teal-pale text-kaycoo-teal-dark border-kaycoo-teal">
              {sourceLabel}
            </Badge>
            <Badge variant="outline" className="text-[10px] shrink-0">{entry.category}</Badge>
            {entry.d365_modules?.slice(0, 1).map((m) => (
              <Badge key={m} variant="outline" className="text-[10px] shrink-0 hidden sm:inline-flex">{m}</Badge>
            ))}
            {entry.d365_versions?.slice(0, 1).map((v) => (
              <Badge key={v} variant="outline" className="text-[10px] shrink-0 hidden md:inline-flex">{v}</Badge>
            ))}
          </div>
        </div>
        <span className="text-[10px] text-kaycoo-charcoal-2 shrink-0 hidden sm:block">
          {new Date(entry.created_at).toLocaleDateString()}
        </span>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}
            className="h-7 gap-1 px-2 text-xs text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale">
            {expanded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {expanded ? 'Close' : 'View'}
          </Button>
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(entry.id)}
              className="h-7 px-2 text-xs text-kaycoo-burgundy hover:bg-kaycoo-burgundy-pale">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-kaycoo-white-3 p-4 space-y-4 bg-gray-50">
          {entry.content && (
            <div>
              <h4 className="text-xs font-semibold text-kaycoo-charcoal mb-1.5">Content</h4>
              <div className="rounded-lg bg-white p-3 text-xs text-kaycoo-charcoal leading-relaxed max-h-[300px] overflow-y-auto whitespace-pre-wrap border border-kaycoo-white-3">
                {decodeHtml(entry.content)}
              </div>
            </div>
          )}

          {embeddedImages && embeddedImages.length > 0 && (
            <div>
              <h4 className="flex items-center gap-1.5 text-xs font-semibold text-kaycoo-charcoal mb-1.5">
                <Image className="h-3.5 w-3.5" />
                Embedded Images ({embeddedImages.length})
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {embeddedImages.map((img, i) => (
                  <EmbeddedImageView key={i} imagePath={img.path} name={img.name} ocrText={img.ocrText} />
                ))}
              </div>
            </div>
          )}

          {entry.source_type === 'document_import' && (
            <div>
              <h4 className="flex items-center gap-1.5 text-xs font-semibold text-kaycoo-charcoal mb-1.5">
                <FileText className="h-3.5 w-3.5" />
                Document Chunks ({chunksQuery.data?.length ?? '...'})
              </h4>
              {chunksQuery.isLoading && <p className="text-xs text-kaycoo-charcoal-2">Loading...</p>}
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {chunksQuery.data?.map((chunk) => (
                  <details key={chunk.id} className="rounded-lg border border-kaycoo-white-3 bg-white">
                    <summary className="cursor-pointer px-3 py-1.5 text-xs font-medium text-kaycoo-charcoal-2 hover:bg-kaycoo-white-2 rounded-lg">
                      Chunk {chunk.chunk_index + 1} — {chunk.token_count} tokens
                    </summary>
                    <div className="px-3 py-2 text-xs text-kaycoo-charcoal leading-relaxed whitespace-pre-wrap border-t border-kaycoo-white-3">
                      {decodeHtml(chunk.content)}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function EmbeddedImageView({ imagePath, name, ocrText }: { imagePath: string; name: string; ocrText: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [showOcr, setShowOcr] = useState(false)

  useEffect(() => {
    supabase.storage.from('customer-documents').createSignedUrl(imagePath, 3600)
      .then(({ data }) => { if (data?.signedUrl) setUrl(data.signedUrl) })
  }, [imagePath])

  return (
    <div className="rounded-lg border border-kaycoo-white-3 bg-white p-2">
      {url ? (
        <img src={url} alt={name} className="w-full rounded object-contain max-h-[150px]" />
      ) : (
        <div className="flex h-[80px] items-center justify-center text-xs text-kaycoo-charcoal-2">Loading...</div>
      )}
      <p className="mt-1 text-[10px] text-kaycoo-charcoal-2 truncate">{name}</p>
      {ocrText && (
        <>
          <button onClick={() => setShowOcr(!showOcr)}
            className="mt-0.5 text-[10px] text-kaycoo-teal-dark hover:underline">
            {showOcr ? 'Hide OCR' : 'Show OCR'}
          </button>
          {showOcr && (
            <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-1.5 text-[10px] text-kaycoo-charcoal max-h-[80px] overflow-y-auto">
              {ocrText}
            </pre>
          )}
        </>
      )}
    </div>
  )
}
